/**

 * 2026-06-02 백업(정상) 관제 필터 + 서버 스냅샷 하이브리드.

 * 물결·레인·중복 swimmer 오탐을 줄이고 실제 수영자는 유지.

 */



type DetLike = {

  bbox: number[];

  score?: number;

  confidence?: number;

  class_name?: string;

  track_id?: number;

  id?: number;

};



function bboxMetrics(bbox: number[], frameW: number, frameH: number) {

  const [x1, y1, x2, y2] = bbox.map(Number);

  const w = Math.max(0, x2 - x1);

  const h = Math.max(0, y2 - y1);

  const fw = Math.max(1, frameW);

  const fh = Math.max(1, frameH);

  const cx = ((x1 + x2) * 0.5) / fw;

  const cy = ((y1 + y2) * 0.5) / fh;

  const aspect = w / Math.max(1, h);

  const area = w * h;

  return { w, h, cx, cy, aspect, area, x1, y1, x2, y2 };

}



function detScore(d: DetLike): number {

  return Number(d.score ?? d.confidence ?? 0);

}



function iou(a: number[], b: number[]): number {

  const x1 = Math.max(a[0], b[0]);

  const y1 = Math.max(a[1], b[1]);

  const x2 = Math.min(a[2], b[2]);

  const y2 = Math.min(a[3], b[3]);

  const iw = Math.max(0, x2 - x1);

  const ih = Math.max(0, y2 - y1);

  const inter = iw * ih;

  const areaA = Math.max(0, a[2] - a[0]) * Math.max(0, a[3] - a[1]);

  const areaB = Math.max(0, b[2] - b[0]) * Math.max(0, b[3] - b[1]);

  const union = areaA + areaB - inter;

  return union > 0 ? inter / union : 0;

}



export function backupStyleWaterRippleFp(

  bbox: number[],

  conf: number,

  frameW: number,

  frameH: number

): boolean {

  const { h, aspect, area } = bboxMetrics(bbox, frameW, frameH);

  const areaRatio = area / Math.max(1, frameW * frameH);

  if (aspect >= 1.35 && h >= 22 && conf >= 0.3) return false;

  if (h < 26 && aspect > 1.8) return true;

  if (areaRatio < 0.0012 && aspect > 2.2) return true;

  if (conf < 0.48 && h < 28 && aspect > 2.4) return true;

  return false;

}



/** 가로형 레인 띠·물결 person/swimmer (하단 넓은 박스)
 *
 * 주의: yolov8n 등 COCO person 수영자는 가로(aspect>1.3)·하단(cy>0.46)인 경우가 많아
 * 예전 조건(aspect≥1.28 && w≥9% && h≤105 → 전부 FP)은 실제 사람을 전부 지웠음.
 * 얇고 매우 납작한 레인/반사 띠만 제거하도록 좁힘.
 */
export function horizontalLaneWaterStripeFp(
  bbox: number[],
  conf: number,
  frameW: number,
  frameH: number
): boolean {
  const { w, h, cy, aspect, area } = bboxMetrics(bbox, frameW, frameH);
  const fw = Math.max(1, frameW);
  const fh = Math.max(1, frameH);
  const areaRatio = area / Math.max(1, fw * fh);
  const wRatio = w / fw;

  if (cy < 0.46) return false;
  // 사람 크기·비율이면 레인으로 보지 않음
  if (h >= 36 && aspect <= 3.2 && areaRatio >= 0.0009 && conf >= 0.22) return false;
  if (h >= 28 && aspect <= 2.4 && conf >= 0.28) return false;
  if (conf >= 0.55 && h >= 24 && areaRatio >= 0.0007) return false;

  // 얇고 긴 띠만
  if (aspect >= 4.0 && h <= 28 && wRatio >= 0.12) return true;
  if (aspect >= 3.2 && h <= 18 && wRatio >= 0.15) return true;
  if (aspect >= 2.8 && h <= 14 && wRatio >= 0.18 && conf < 0.45) return true;
  if (h <= 12 && wRatio >= 0.2 && aspect >= 2.5) return true;
  return false;
}



/** 상단 레인 물결·바닥줄 고착 swimmer (백엔드 lane_water_fp) */

export function upperLaneWaterSwimmerFp(

  bbox: number[],

  conf: number,

  frameW: number,

  frameH: number

): boolean {

  if (conf < 0.58 || bbox.length !== 4 || frameW < 1 || frameH < 1) {

    return false;

  }

  const { w, h, cx, cy, aspect } = bboxMetrics(bbox, frameW, frameH);

  const fw = Math.max(1, frameW);

  const wRatio = w / fw;

  if (cx < 0.36 || cx > 0.62) return false;

  if (cy < 0.19 || cy > 0.44) return false;

  if (wRatio >= 0.122 && aspect >= 1.1 && h >= 68 && h <= 112) return true;

  if (wRatio >= 0.1 && aspect >= 1.38 && h >= 62 && h <= 108) return true;

  if (

    wRatio >= 0.095 &&

    wRatio <= 0.115 &&

    aspect >= 1.12 &&

    aspect <= 1.72 &&

    h >= 86 &&

    h <= 124 &&

    conf >= 0.78

  ) {

    return true;

  }

  return false;

}



/** detector_ver5 중앙 레인·바닥줄 고착 person/swimmer (백엔드 lane_stripe_lock_fp) */

export function ver5LaneStripePersonLockFp(

  bbox: number[],

  conf: number,

  frameW: number,

  frameH: number

): boolean {

  if (conf < 0.87 || bbox.length !== 4 || frameW < 1 || frameH < 1) {

    return false;

  }

  const { w, h, cx, cy, aspect } = bboxMetrics(bbox, frameW, frameH);

  if (cy >= 0.46) return false;

  if (cx < 0.39 || cx > 0.54) return false;

  if (cy < 0.26 || cy > 0.43) return false;

  if (aspect < 0.48 || aspect > 0.86) return false;

  if (h < 76 || h > 124) return false;

  if (w < 50 || w > 80) return false;

  return true;

}



export function lowerPoolEdgeRipplePersonFp(

  bbox: number[],

  conf: number,

  frameW: number,

  frameH: number

): boolean {

  if (conf >= 0.46 || bbox.length !== 4 || frameH < 1) return false;

  const { h, cy, aspect } = bboxMetrics(bbox, frameW, frameH);

  if (cy < 0.68) return false;

  if (aspect > 1.22) return false;

  if (h < 18 || h > 95) return false;

  return true;

}



/** 백엔드 _is_deck_structure_person_fp 와 동일 목적(프론트 2차) */

export function deckStructurePersonFp(

  bbox: number[],

  conf: number,

  frameW: number,

  frameH: number

): boolean {

  if (conf < 0.82 || bbox.length !== 4 || frameW < 1 || frameH < 1) {

    return false;

  }

  const { w, h, cx, cy, aspect } = bboxMetrics(bbox, frameW, frameH);

  const fw = Math.max(1, frameW);

  const fh = Math.max(1, frameH);

  const wRatio = w / fw;

  const hRatio = h / fh;

  if (cy >= 0.5) return false;

  if (cx < 0.34 || cx > 0.6) return false;

  if (cy < 0.24 || cy > 0.48) return false;

  if (aspect < 0.55 || aspect > 1.02) return false;

  if (wRatio < 0.04 || wRatio > 0.092) return false;

  if (hRatio < 0.088 || hRatio > 0.17) return false;

  if (h > 86 || w > 74) return false;

  if (h < 52 || w < 38) return false;

  return true;

}



/** 상단 레인: 인접한 고신뢰 swimmer 중 면적 작은 물결 오탐 제거 */

function clusterDedupeUpperLane(

  dets: DetLike[],

  frameW: number,

  frameH: number

): DetLike[] {

  const upper = dets

    .map((d, idx) => ({ d, idx, m: bboxMetrics(d.bbox, frameW, frameH) }))

    .filter((x) => x.m.cy < 0.46);

  if (upper.length < 2) return dets;

  const removeIdx = new Set<number>();

  const distPx = (a: typeof upper[0], b: typeof upper[0]) => {

    const ax = (a.m.x1 + a.m.x2) * 0.5;

    const ay = (a.m.y1 + a.m.y2) * 0.5;

    const bx = (b.m.x1 + b.m.x2) * 0.5;

    const by = (b.m.y1 + b.m.y2) * 0.5;

    return Math.hypot(ax - bx, ay - by);

  };

  for (let i = 0; i < upper.length; i++) {

    for (let j = i + 1; j < upper.length; j++) {

      const a = upper[i];

      const b = upper[j];

      if (distPx(a, b) > 110) continue;

      if (iou(a.d.bbox, b.d.bbox) > 0.45) continue;

      const sa = detScore(a.d);

      const sb = detScore(b.d);

      const drop =

        a.m.area < b.m.area

          ? a

          : b.m.area < a.m.area

            ? b

            : sa < sb

              ? a

              : b;

      removeIdx.add(drop.idx);

    }

  }

  if (!removeIdx.size) return dets;

  return dets.filter((_, idx) => !removeIdx.has(idx));

}



/** IoU·포함 관계 중복 박스 정리 */

function dedupeOverlapping(dets: DetLike[], iouThr = 0.32): DetLike[] {

  const sorted = [...dets].sort((a, b) => detScore(b) - detScore(a));

  const kept: DetLike[] = [];

  for (const c of sorted) {

    const dup = kept.some((k) => iou(k.bbox, c.bbox) >= iouThr);

    if (!dup) kept.push(c);

  }

  return kept;

}



export function filterHybridDetectionList<T extends DetLike>(
  dets: T[],
  frameW: number,
  frameH: number,
  filterStrength = 1
): T[] {
  let list = dets.filter((d) => Array.isArray(d.bbox) && d.bbox.length === 4);
  list = list.filter((d) => {
    const conf = detScore(d);
    const cn = String(d.class_name || 'person').toLowerCase();
    const bb = d.bbox as number[];
    const { h, area } = bboxMetrics(bb, frameW, frameH);
    const areaRatio = area / Math.max(1, frameW * frameH);

    // 레인/물결 FP 완화용 — 고깔·시설물·데크는 항상 검사
    const laneRippleBypass =
      (cn === 'person' || cn === 'swimmer') &&
      conf >= 0.38 &&
      h >= 22 &&
      areaRatio >= 0.00055;

    if (coneOrFacilityStructureFp(bb, conf, frameW, frameH, filterStrength)) return false;
    if (obliqueWideStructureFp(bb, conf, frameW, frameH, filterStrength)) return false;
    if (poolFloatConeOrBuoyFp(bb, conf, frameW, frameH)) return false;
    if (deckStructurePersonFp(bb, conf, frameW, frameH)) return false;
    if (!laneRippleBypass && ver5LaneStripePersonLockFp(bb, conf, frameW, frameH)) return false;
    if (!laneRippleBypass && lowerPoolEdgeRipplePersonFp(bb, conf, frameW, frameH)) return false;
    if (!laneRippleBypass && backupStyleWaterRippleFp(bb, conf, frameW, frameH)) return false;
    if (!laneRippleBypass && horizontalLaneWaterStripeFp(bb, conf, frameW, frameH)) return false;
    if (
      !laneRippleBypass &&
      (cn === 'swimmer' || cn === 'person') &&
      upperLaneWaterSwimmerFp(bb, conf, frameW, frameH)
    ) {
      return false;
    }
    const { cy } = bboxMetrics(bb, frameW, frameH);
    if (cn === 'person' && conf < 0.32 && cy > 0.82) return false;
    return true;
  }) as T[];
  list = clusterDedupeUpperLane(list, frameW, frameH) as T[];
  list = dedupeOverlapping(list, 0.34) as T[];
  return list;
}



/** 튜브·꼬깔콘·소형 부유물(탑뷰/사선 정사각~원형 blob) — yolov8n COCO 오탐.
 * 서서 걷는 사람·활동하는 사람 크기는 절대 제거하지 않음.
 */
export function poolFloatConeOrBuoyFp(
  bbox: number[],
  conf: number,
  frameW: number,
  frameH: number
): boolean {
  const { w, h, cy, aspect, area } = bboxMetrics(bbox, frameW, frameH);
  const fw = Math.max(1, frameW);
  const fh = Math.max(1, frameH);
  const areaRatio = area / Math.max(1, fw * fh);
  const longest = Math.max(w, h);
  const shortest = Math.min(w, h);
  if (cy < 0.35) return false;
  // 사람 크기면 부유물로 보지 않음
  if (conf >= 0.22 && h >= 20 && areaRatio >= 0.0005) return false;
  if (aspect <= 1.15 && h >= 22 && conf >= 0.18) return false;
  if (aspect >= 1.2 && h >= 22 && conf >= 0.28) return false;
  // 아주 작은 원형·정사각만
  if (
    aspect >= 0.7 &&
    aspect <= 1.35 &&
    longest <= 36 &&
    shortest >= 10 &&
    h <= 34 &&
    areaRatio <= 0.0015 &&
    conf < 0.5
  ) {
    return true;
  }
  if (aspect >= 2.5 && h <= 18 && w >= 0.12 * fw && conf < 0.5) {
    return true;
  }
  return false;
}

function coneFpConfCaps(filterStrength: number): { narrow: number; edge: number } {
  const s = Math.max(0, Math.min(2, Math.floor(filterStrength)));
  if (s <= 0) return { narrow: 0.68, edge: 0.55 };
  if (s >= 2) return { narrow: 0.88, edge: 0.72 };
  return { narrow: 0.75, edge: 0.62 };
}

function facilityFpConfCap(filterStrength: number): number {
  const s = Math.max(0, Math.min(2, Math.floor(filterStrength)));
  if (s <= 0) return 0.48;
  if (s >= 2) return 0.65;
  return 0.55;
}

/** 백엔드 _is_likely_traffic_cone_or_deck_marker_fp — 데크 꼬깔·폴대 */
export function trafficConeOrDeckMarkerFp(
  bbox: number[],
  conf: number,
  frameW: number,
  frameH: number,
  filterStrength = 1
): boolean {
  const { w, h, cx, cy, aspect, area } = bboxMetrics(bbox, frameW, frameH);
  const fw = Math.max(1, frameW);
  const fh = Math.max(1, frameH);
  const areaRatio = area / Math.max(1, fw * fh);
  const { narrow, edge } = coneFpConfCaps(filterStrength);
  if (aspect >= 0.85) return false;
  const uprightLike =
    aspect >= 0.36 && conf >= 0.45 && h / fh >= 0.22 && aspect <= 1.12 && h >= 28;
  if (uprightLike) return false;
  if (
    aspect <= 0.42 &&
    w <= 0.11 * fw &&
    h >= 20 &&
    h <= 0.42 * fh &&
    conf < narrow
  ) {
    return true;
  }
  const nearEdge = cy >= 0.58 || cx <= 0.08 || cx >= 0.92;
  if (
    nearEdge &&
    aspect <= 0.5 &&
    h <= 0.28 * fh &&
    w <= 0.1 * fw &&
    areaRatio <= 0.012 &&
    conf < edge
  ) {
    return true;
  }
  return false;
}

/** 백엔드 _is_likely_facility_structure_fp — 천막·기둥·난간 */
export function facilityStructureFp(
  bbox: number[],
  conf: number,
  frameW: number,
  frameH: number,
  filterStrength = 1
): boolean {
  const { w, h, cy, aspect, area } = bboxMetrics(bbox, frameW, frameH);
  const fw = Math.max(1, frameW);
  const fh = Math.max(1, frameH);
  const areaRatio = area / Math.max(1, fw * fh);
  const structCap = facilityFpConfCap(filterStrength);
  const uprightLike = aspect >= 0.36 && conf >= 0.42 && h >= 28;
  if (uprightLike) return false;
  if (cy <= 0.22 && conf < structCap && areaRatio >= 0.008) return true;
  if (aspect <= 0.25 && h >= 0.14 * fh && conf < structCap) return true;
  if (aspect >= 3.0 && h <= 0.08 * fh && conf < Math.min(0.5, structCap)) return true;
  return false;
}

export function coneOrFacilityStructureFp(
  bbox: number[],
  conf: number,
  frameW: number,
  frameH: number,
  filterStrength = 1
): boolean {
  return (
    trafficConeOrDeckMarkerFp(bbox, conf, frameW, frameH, filterStrength) ||
    facilityStructureFp(bbox, conf, frameW, frameH, filterStrength)
  );
}

/** 백엔드 _is_coco_oblique_wide_structure_fp — 넓은 바닥·시설 띠 person 오탐 */
export function obliqueWideStructureFp(
  bbox: number[],
  conf: number,
  frameW: number,
  frameH: number,
  filterStrength = 1
): boolean {
  const { w, h, aspect } = bboxMetrics(bbox, frameW, frameH);
  const fw = Math.max(1, frameW);
  const fh = Math.max(1, frameH);
  const hRatio = h / fh;
  const wRatio = w / fw;
  const areaRatio = (w * h) / Math.max(1, fw * fh);
  const s = Math.max(0, Math.min(2, Math.floor(filterStrength)));
  const uprightLike =
    aspect >= 0.36 && conf >= 0.38 && hRatio >= 0.03 && h >= 24 && aspect <= 1.15;
  if (uprightLike) return false;
  const plausiblePerson = aspect >= 0.35 && conf >= 0.4 && h >= 24;
  if (plausiblePerson) return false;
  const areaGate = s >= 2 ? 0.085 : s >= 1 ? 0.1 : 0.12;
  const wideGate = s >= 2 ? 0.46 : s >= 1 ? 0.52 : 0.58;
  const confCap = s >= 2 ? 0.68 : s >= 1 ? 0.62 : 0.56;
  if (areaRatio >= areaGate && conf < confCap) return true;
  if (wRatio >= wideGate && conf < confCap + 0.06) {
    if (aspect >= 0.85 || hRatio < 0.34) return true;
  }
  if (wRatio >= 0.68 && conf < 0.82) return true;
  if (aspect >= 2.35 && hRatio <= 0.22 && wRatio >= 0.34 && conf < 0.72) return true;
  return false;
}

export function obliquePlaygroundFp(
  bbox: number[],
  conf: number,
  frameW: number,
  frameH: number,
  filterStrength = 1
): boolean {
  return (
    coneOrFacilityStructureFp(bbox, conf, frameW, frameH, filterStrength) ||
    obliqueWideStructureFp(bbox, conf, frameW, frameH, filterStrength)
  );
}

export function hybridRefineClassName(
  apiClass: string,
  bbox: number[],
  conf: number,
  frameW: number,
  frameH: number,
  filterStrength = 1
): string | null {
  const cn = (apiClass || 'person').toLowerCase();
  const { aspect, area, h } = bboxMetrics(bbox, frameW, frameH);
  const areaRatio = area / Math.max(1, frameW * frameH);

  if (coneOrFacilityStructureFp(bbox, conf, frameW, frameH, filterStrength)) return null;
  if (deckStructurePersonFp(bbox, conf, frameW, frameH)) return null;

  // 얇은 레인 띠만 제거 — 사람 크기는 유지
  const humanSized = conf >= 0.2 && h >= 16 && areaRatio >= 0.0004;
  if (!humanSized) {
    if (poolFloatConeOrBuoyFp(bbox, conf, frameW, frameH)) return null;
    if (backupStyleWaterRippleFp(bbox, conf, frameW, frameH)) return null;
    if (horizontalLaneWaterStripeFp(bbox, conf, frameW, frameH)) return null;
  } else {
    if (horizontalLaneWaterStripeFp(bbox, conf, frameW, frameH)) return null;
    if (poolFloatConeOrBuoyFp(bbox, conf, frameW, frameH)) return null;
  }
  if (!humanSized && upperLaneWaterSwimmerFp(bbox, conf, frameW, frameH)) return null;

  if (!humanSized && conf < 0.32 && h < 20) return null;
  if (!humanSized && areaRatio < 0.00035 && conf < 0.5) return null;

  if (cn === 'swimmer' || cn === 'drowning' || cn === 'lifeguard') return cn;
  if (aspect <= 1.15) return 'person';
  if (aspect >= 1.35 && areaRatio >= 0.0009 && conf >= 0.28) return 'swimmer';
  if (aspect < 1.35) return 'person';
  if (conf >= 0.4 && aspect >= 1.15) return 'swimmer';
  return 'person';
}


